import React from "react";
import dayjs from "dayjs";

import {
  Typography,
  Avatar,
  TableRow,
  TableCell,
  useMediaQuery,
  IconButton,
  Box,
  Tooltip,
} from "@mui/material";
import { makeStyles, styled } from "@mui/styles";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import CancelIcon from "@mui/icons-material/Cancel";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import ButtonBlue from "../../../shared/buttonBlue";

// Styled Component
const useStyles = makeStyles((theme) => ({}));

const RowStyled = styled(TableRow)(({ theme }) => ({
  width: "100%",
  height: "120px",
  cursor: "pointer",
  transition:
    "transform 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  "&:hover": {
    boxShadow: "0 0 10px 0 rgba(73, 73, 73, 0.8)",
  },
  [theme.breakpoints.down("lg")]: {
    height: "fit-content",
    flexDirection: "column",
    paddingBlock: "30px",
  },
  [theme.breakpoints.down("md")]: {
    borderBottom: `1px solid #c4c9cc`,
  },
}));

const AvatarStyled = styled(Avatar)(({ theme }) => ({
  width: "40px",
  height: "40px",
}));

const TextNormal = styled(Typography)(({ theme, color }) => ({
  fontSize: "16px",
  color: theme.palette.text[color] || theme.palette.text["primary"],
  [theme.breakpoints.down("md")]: {
    fontSize: "14px",
  },
}));

const TextSmall = styled(Typography)(({ theme, color }) => ({
  fontSize: "14px",
  color: theme.palette.text[color] || theme.palette.text["third"],
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
  },
}));

const EditButton = styled(IconButton)(({ theme }) => ({
  width: "35px",
  height: "35px",
  backgroundColor: theme.palette.grey[500],
  ["&:hover"]: {
    backgroundColor: theme.palette.grey[700],
  },
}));

const DeleteButton = styled(IconButton)({
  width: "35px",
  height: "35px",
  backgroundColor: "red",
  ["&:hover"]: {
    backgroundColor: "darkred",
  },
});

const CancelButton = styled(IconButton)({
  width: "35px",
  height: "35px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "red",
  ["&:hover"]: {
    backgroundColor: "darkred",
  },
});

const StatusText = styled(Typography)(({ theme, status, typeOfBooking }) => ({
  fontSize: "15px",
  textAlign: "left",
  color:
    typeOfBooking === "DeliverySampleShuttleCar"
      ? status === "ได้รับสินค้าเรียบร้อย"
        ? "#4CAF50" // Green
        : status === "ส่งสินค้าเรียบร้อย"
        ? "#2196F3" // Blue
        : status === "รับสินค้าเรียบร้อย"
        ? "#FF9800" // Orange
        : status === "รอรับสินค้า"
        ? "#F44336" // Red
        : "inherit"
      : typeOfBooking === "BetweenSiteCar"
      ? status === "รายการเสร็จสิ้น"
        ? "#4CAF50" // Green
        : status === "กำลังดำเนินการ"
        ? "#FF9800" // Orange
        : status === "ยกเลิกรายการ"
        ? "#8f8f8f" // Grey
        : status === "รอการจัดหาคนขับรถ"
        ? "#F44336" // Red
        : "inherit"[theme?.breakpoints?.down("sm")]
      : {
          fontSize: "14px",
        },
}));

const SiteInfo = ({ iconColor, siteName }) => (
  <div
    style={{ display: "flex", alignItems: "flex-start", marginBottom: "5px" }}
  >
    <LocationOnIcon
      sx={{
        height: "20px",
        color: iconColor,
        verticalAlign: "middle",
        marginRight: "4px",
      }}
    />
    <TextSmall color="primary">{siteName}</TextSmall>
  </div>
);

const Body = ({
  bookingText,
  typeOfBooking,
  onClickCardHandler,
  onClickOpenCancelModal,
  setCancelBookingId,
  handleEditBooking,
  onClickOpenDeleteModal,
  setDeletingBookingId,
  listOfAction,
}) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));

  return isMobile ? (
    <RowStyled
      sx={{
        "& .MuiTableCell-root": {
          width: "100%",
        },
      }}
      onClick={() => onClickCardHandler(typeOfBooking, bookingText.bookingId)}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "12px",
        }}
      >
        <AvatarStyled
          src={`${process.env.REACT_APP_URL}image/profile/1.jpg`}
          alt="Profile"
          style={{ width: "50px", height: "50px", marginBlock: "12px" }}
        />
        <div
          style={{
            paddingLeft: "12px",
            display: "flex",
            flexDirection: "column",
            width: "100%",
            gap: "12px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
            }}
          >
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              <TextNormal>
                <strong> {bookingText.nameLabel} </strong>
                {bookingText.name}
              </TextNormal>
              {bookingText.email && (
                <TextNormal>
                  <strong>อีเมล </strong>
                  {bookingText.email}
                </TextNormal>
              )}
              <TextNormal>
                <strong>เบอร์โทรศัพท์ </strong>
                {bookingText.phone}
              </TextNormal>
              {bookingText.sampleName && (
                <TextNormal>
                  <strong>ชื่อตัวอย่าง </strong>
                  {bookingText.sampleName}
                </TextNormal>
              )}
            </div>
            {bookingText.status && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "2px",
                  marginTop: "10px",
                  maxWidth: "100px",
                  justifyContent: "flex-end",
                }}
              >
                {bookingText.status === "ได้รับสินค้าเรียบร้อย" ||
                bookingText.status === "รายการเสร็จสิ้น" ? (
                  <CheckCircleRoundedIcon
                    style={{ color: "#4CAF50", fontSize: "18px" }}
                  />
                ) : (
                  <HourglassTopRoundedIcon
                    style={{
                      fontSize: "18px",
                      color:
                        bookingText.status === "ส่งสินค้าเรียบร้อย"
                          ? "#2196F3"
                          : bookingText.status === "รับสินค้าเรียบร้อย" ||
                            bookingText.status === "กำลังดำเนินการ"
                          ? "#FF9800"
                          : bookingText.status === "รอรับสินค้า" ||
                            bookingText.status === "รอการจัดหาคนขับรถ"
                          ? "#F44336"
                          : bookingText.status === "ยกเลิกรายการ"
                          ? "#8f8f8f"
                          : "inherit",
                    }}
                  />
                )}
                <StatusText
                  status={bookingText.status}
                  style={{ fontSize: "14px", fontWeight: "500" }}
                  typeOfBooking={typeOfBooking}
                >
                  {bookingText.status}
                </StatusText>
              </div>
            )}
          </div>
          {bookingText.driver && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <TextNormal style={{ fontSize: "14px" }}>
                <strong>คนขับรถ </strong>
                {bookingText.driver}
              </TextNormal>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <TextNormal style={{ fontSize: "14px" }}>
              <strong>{bookingText.startDateLabel}</strong>
              {bookingText.startTime
                ? `${bookingText.startDate} ${bookingText.startTime}`
                : bookingText.startDate}
            </TextNormal>
            {bookingText.returnDate && (
              <TextNormal style={{ fontSize: "14px" }}>
                <strong>{bookingText.returnDateLabel}</strong>
                {bookingText.returnTime
                  ? `${bookingText.returnDate} ${bookingText.returnTime}`
                  : bookingText.returnTime}
              </TextNormal>
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <SiteInfo iconColor="green" siteName={bookingText.fromPlace} />
            <SiteInfo iconColor="red" siteName={bookingText.toPlace} />
          </div>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {bookingText.typeFlight && (
              <TextNormal>
                <strong>{bookingText.typeFlight}</strong>
              </TextNormal>
            )}
            {bookingText.typeProduct && (
              <TextNormal>
                <strong>{bookingText.typeProduct}</strong>
              </TextNormal>
            )}
            {bookingText.numberOfPassenger && (
              <TextNormal>
                <strong>{bookingText.numberOfPassenger}</strong>
              </TextNormal>
            )}
            {bookingText.weightProduct && (
              <TextNormal>
                <strong>{bookingText.weightProduct}</strong>
              </TextNormal>
            )}
          </div>
        </div>
      </div>
      <Box
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "10px",
          justifyContent: "flex-end",
        }}
      >
        {listOfAction.includes("edit") && (
          <ButtonBlue
            variant="outlined"
            customColor="#9e9e9e"
            sx={{
              fontSize: "14px",
              "&:hover": {
                backgroundColor: "##c8c8c81a",
                borderColor: "##c8c8c8",
              },
            }}
            onClick={(event) => {
              event.stopPropagation();
              handleEditBooking(bookingText.bookingId);
            }}
          >
            แก้ไขรายการ
          </ButtonBlue>
        )}
        {listOfAction.includes("delete") && (
          <ButtonBlue
            variant="outlined"
            customColor="#F44336"
            sx={{
              fontSize: "14px",
              "&:hover": {
                backgroundColor: "#ff091105",
                borderColor: "#d9392e",
              },
            }}
            onClick={(event) => {
              event.stopPropagation();
              onClickOpenDeleteModal();
              setDeletingBookingId(bookingText.bookingId);
            }}
          >
            ลบรายการ
          </ButtonBlue>
        )}
        {listOfAction.includes("cancel") && (
          <ButtonBlue
            variant="outlined"
            customColor="#F44336"
            sx={{
              fontSize: "14px",
              "&:hover": {
                backgroundColor: "#ff091105",
                borderColor: "#d9392e",
              },
            }}
            onClick={(event) => {
              event.stopPropagation();
              onClickOpenCancelModal();
              setCancelBookingId(bookingText.bookingId);
            }}
          >
            ยกเลิกรายการ
          </ButtonBlue>
        )}
      </Box>
    </RowStyled>
  ) : (
    // !isMobile (md)
    <RowStyled
      sx={{
        "& .MuiTableCell-root": {
          borderBottom: "none",
          borderTop: "none",
        },
      }}
      onClick={() => onClickCardHandler(typeOfBooking, bookingText.bookingId)}
    >
      <TableCell>
        <Typography
          style={{ display: "flex", alignItems: "center", gap: "10px" }}
        >
          <AvatarStyled
            src={`${process.env.REACT_APP_URL}image/profile/1.jpg`}
            alt="1"
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <TextNormal>{bookingText.name}</TextNormal>
            {bookingText.email && (
              <TextSmall>{`${"อีเมล: " + bookingText.email}`}</TextSmall>
            )}
            <TextSmall>{`${"เบอร์โทรศัพท์: " + bookingText.phone}`}</TextSmall>
          </div>
        </Typography>
      </TableCell>
      {bookingText.sampleName && (
        <TableCell>
          <TextNormal>{bookingText.sampleName}</TextNormal>
        </TableCell>
      )}
      {bookingText.driver && (
        <TableCell>
          <TextNormal>{bookingText.driver}</TextNormal>
        </TableCell>
      )}
      <TableCell>
        <TextNormal>{bookingText.startDate}</TextNormal>
        {bookingText.startTime && (
          <TextSmall>{bookingText.startTime}</TextSmall>
        )}
      </TableCell>
      {bookingText.returnDate &&
        (bookingText.returnDate === "Invalid Date" ? (
          <TableCell>
            <TextNormal>-</TextNormal>
          </TableCell>
        ) : (
          <TableCell>
            <TextNormal>{bookingText.returnDate}</TextNormal>
            {bookingText.returnTime && (
              <TextSmall>{bookingText.returnTime}</TextSmall>
            )}
          </TableCell>
        ))}

      <TableCell>
        <SiteInfo iconColor="green" siteName={bookingText.fromPlace} />
        <SiteInfo iconColor="red" siteName={bookingText.toPlace} />
      </TableCell>
      {bookingText.status && (
        <TableCell>
          <TextNormal sx={{ display: "flex" }}>
            {bookingText.status === "ได้รับสินค้าเรียบร้อย" ||
            bookingText.status === "รายการเสร็จสิ้น" ? (
              <CheckCircleRoundedIcon
                style={{ color: "#4CAF50", marginRight: "5px" }}
              />
            ) : (
              <HourglassTopRoundedIcon
                style={{
                  fontSize: "18px",
                  color:
                    bookingText.status === "ส่งสินค้าเรียบร้อย"
                      ? "#2196F3"
                      : bookingText.status === "รับสินค้าเรียบร้อย" ||
                        bookingText.status === "กำลังดำเนินการ"
                      ? "#FF9800"
                      : bookingText.status === "รอรับสินค้า" ||
                        bookingText.status === "รอการจัดหาคนขับรถ"
                      ? "#F44336"
                      : bookingText.status === "ยกเลิกรายการ"
                      ? "#8f8f8f"
                      : "inherit",
                }}
              />
            )}
            <StatusText
              status={bookingText.status}
              typeOfBooking={typeOfBooking}
            >
              {bookingText.status}
            </StatusText>
          </TextNormal>
        </TableCell>
      )}
      {bookingText.typeFlight && (
        <TableCell>
          <TextNormal>{bookingText.typeFlight}</TextNormal>
        </TableCell>
      )}
      {bookingText.typeProduct && (
        <TableCell>
          <TextNormal>{bookingText.typeProduct}</TextNormal>
        </TableCell>
      )}
      {bookingText.numberOfPassenger && (
        <TableCell>
          <TextNormal>{bookingText.numberOfPassenger}</TextNormal>
        </TableCell>
      )}
      {bookingText.weightProduct && (
        <TableCell>
          <TextNormal>{bookingText.weightProduct}</TextNormal>
        </TableCell>
      )}
      <TableCell>
        <Box style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
          {listOfAction.includes("edit") && (
            <Tooltip title="แก้ไขรายการ">
              <EditButton
                onClick={(event) => {
                  event.stopPropagation();
                  handleEditBooking(bookingText.bookingId);
                }}
                size="large"
              >
                <EditIcon sx={{ fontSize: "20px", color: "white" }} />
              </EditButton>
            </Tooltip>
          )}
          {listOfAction.includes("delete") && (
            <Tooltip title="ลบรายการ">
              <DeleteButton
                onClick={(event) => {
                  event.stopPropagation();
                  onClickOpenDeleteModal();
                  setDeletingBookingId(bookingText.bookingId);
                }}
                size="large"
              >
                <DeleteForeverIcon sx={{ fontSize: "20px", color: "white" }} />
              </DeleteButton>
            </Tooltip>
          )}
          {listOfAction.includes("cancel") && (
            <Tooltip title="ยกเลิกรายการ">
              <CancelButton
                onClick={(event) => {
                  event.stopPropagation();
                  onClickOpenCancelModal();
                  setCancelBookingId(bookingText.bookingId);
                }}
                size="large"
              >
                <CancelIcon sx={{ fontSize: "20px", color: "white" }} />
              </CancelButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    </RowStyled>
  );
};

export default Body;
